import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { getPriorityConfig } from "../../utils/bannerMessages";

const truncated = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// A hairline bottom rule, not a box: a full-width strip has no neighbour to fence off.
const Row = styled.li`
  display: grid;
  grid-template-columns: ${({ $split }) =>
    $split ? "minmax(0, 1fr) auto minmax(0, 1fr)" : "minmax(0, 1fr)"};
  align-items: center;
  column-gap: 10px;
  padding: 10px 16px;
  font-size: ${({ $size }) => $size};
  line-height: 1.4;
  font-weight: ${({ $weight }) => $weight};
  color: ${({ $text }) => $text};
  background-color: ${({ $fill }) => $fill};
  border-bottom: 1px solid ${({ $rule }) => $rule};
`;

const Main = styled.span`
  grid-column: ${({ $split }) => ($split ? 2 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 0;
`;

const Trailing = styled.span`
  grid-column: 3;
  justify-self: end;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const TogglePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78em;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.01em;
  padding: 5px 10px 5px 12px;
  border: none;
  border-radius: 999px;
  color: #ffffff;
  background: ${({ $tone }) => $tone};
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    filter: brightness(1.1);
  }

  &:focus-visible {
    outline: 2px solid ${({ $tone }) => $tone};
    outline-offset: 2px;
  }

  svg {
    transition: transform 0.15s ease;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  }
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.07);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 1px;
  }

  svg {
    transition: transform 0.15s ease;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  }
`;

const Close = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const Chevron = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const Text = styled.span`
  min-width: 0;
  ${({ $truncate }) => $truncate && truncated}
`;

const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// Inline SVG, never CDN icon fonts: the icon is the only non-color severity signal.
const PriorityIcon = ({ variant, color, size }) => {
  if (variant === "bullet") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="12" cy="12" r="4" fill={color} />
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
};

PriorityIcon.propTypes = {
  variant: PropTypes.oneOf(["triangle", "bullet"]).isRequired,
  color: PropTypes.string.isRequired,
  size: PropTypes.number.isRequired,
};

const BannerMessage = ({
  message,
  priority,
  truncate = false,
  hiddenCount = 0,
  expanded = false,
  onToggle = null,
  onDismiss = null,
}) => {
  const config = getPriorityConfig(priority);
  const hasTrailing =
    Boolean(onToggle) || hiddenCount > 0 || Boolean(onDismiss);
  const moreLabel = `${hiddenCount} more alert${hiddenCount === 1 ? "" : "s"}`;
  const toggleLabel = expanded ? "Hide alerts" : `Open ${moreLabel}`;

  return (
    <Row
      $fill={config.fill}
      $rule={config.rule}
      $text={config.text}
      $size={config.fontSize}
      $weight={config.fontWeight}
      $split={hasTrailing}
      data-priority={priority}
      data-testid="banner-message"
    >
      <Main $split={hasTrailing}>
        <IconSlot>
          <PriorityIcon
            variant={config.icon}
            color={config.iconColor}
            size={config.iconSize}
          />
        </IconSlot>
        <SrOnly>{config.srLabel}</SrOnly>
        <Text $truncate={truncate} data-testid="banner-message-text">
          {message}
        </Text>
      </Main>
      {hasTrailing && (
        <Trailing>
          {onToggle && (
            <TogglePill
              type="button"
              $tone={config.text}
              $open={expanded}
              aria-expanded={expanded}
              onClick={onToggle}
            >
              {toggleLabel}
              <Chevron />
            </TogglePill>
          )}
          {onDismiss && (
            <Toggle
              type="button"
              aria-label="Dismiss alerts"
              onClick={onDismiss}
            >
              <Close />
            </Toggle>
          )}
        </Trailing>
      )}
    </Row>
  );
};

BannerMessage.propTypes = {
  message: PropTypes.string.isRequired,
  priority: PropTypes.oneOf([1, 2, 3]).isRequired,
  truncate: PropTypes.bool,
  hiddenCount: PropTypes.number,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  onDismiss: PropTypes.func,
};

export default BannerMessage;
